import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import {
  useAllocateLicenseMutation,
  useGetBusinessQuery,
  useGetLicensesQuery,
} from "../../store/api";
import { getCurrentLicense } from "../../utils/license";

const DEFAULT_LICENSE_FORM = {
  planCode: "PRO",
  startsOn: "",
  endsOn: "",
  graceDays: 7,
  maxStores: 1,
  maxTillsPerStore: 1,
  maxKiosksPerStore: 0,
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const EditLicense = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { data: business } = useGetBusinessQuery(businessId);
  const {
    data,
    isLoading: isLicenseLoading,
    isError: isLicenseError,
    error: licenseError,
  } = useGetLicensesQuery(businessId);
  const [allocateLicense, allocateState] = useAllocateLicenseMutation();

  const [form, setForm] = useState(DEFAULT_LICENSE_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const currentLicense = useMemo(() => getCurrentLicense(data), [data]);

  useEffect(() => {
    if (!currentLicense) return;
    setForm({
      planCode: currentLicense.planCode || currentLicense.planName || "PRO",
      startsOn: toDateInputValue(currentLicense.startsOn),
      endsOn: toDateInputValue(currentLicense.endsOn),
      graceDays: currentLicense.graceDays ?? DEFAULT_LICENSE_FORM.graceDays,
      maxStores: currentLicense.maxStores ?? DEFAULT_LICENSE_FORM.maxStores,
      maxTillsPerStore:
        currentLicense.maxTillsPerStore ?? DEFAULT_LICENSE_FORM.maxTillsPerStore,
      maxKiosksPerStore:
        currentLicense.maxKiosksPerStore ??
        DEFAULT_LICENSE_FORM.maxKiosksPerStore,
    });
  }, [currentLicense]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(form.planCode || "").trim()) {
      nextErrors.planCode = "Plan code is required.";
    }
    if (!form.startsOn) {
      nextErrors.startsOn = "Start date is required.";
    }
    if (!form.endsOn) {
      nextErrors.endsOn = "End date is required.";
    }

    if (form.startsOn && form.endsOn) {
      const startDate = new Date(form.startsOn);
      const endDate = new Date(form.endsOn);
      if (endDate < startDate) {
        nextErrors.endsOn = "End date must be on or after start date.";
      }
    }

    const graceValue = Number(form.graceDays);
    if (form.graceDays !== "" && (Number.isNaN(graceValue) || graceValue < 0)) {
      nextErrors.graceDays = "Grace days must be 0 or greater.";
    }

    const maxStoresValue = Number(form.maxStores);
    if (
      form.maxStores === "" ||
      Number.isNaN(maxStoresValue) ||
      maxStoresValue < 0
    ) {
      nextErrors.maxStores = "Max stores must be 0 or greater.";
    }

    const maxTillsValue = Number(form.maxTillsPerStore);
    if (
      form.maxTillsPerStore === "" ||
      Number.isNaN(maxTillsValue) ||
      maxTillsValue < 0
    ) {
      nextErrors.maxTillsPerStore =
        "Max tills per store must be 0 or greater.";
    }

    const maxKiosksValue = Number(form.maxKiosksPerStore);
    if (
      form.maxKiosksPerStore === "" ||
      Number.isNaN(maxKiosksValue) ||
      maxKiosksValue < 0
    ) {
      nextErrors.maxKiosksPerStore =
        "Max kiosks per store must be 0 or greater.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    if (!validate()) return;

    const payload = {
      planCode: String(form.planCode || "").trim().toUpperCase(),
      startsOn: form.startsOn,
      endsOn: form.endsOn,
      graceDays: form.graceDays === "" ? 0 : Number(form.graceDays),
      maxStores: Number(form.maxStores),
      maxTillsPerStore: Number(form.maxTillsPerStore),
      maxKiosksPerStore: Number(form.maxKiosksPerStore),
    };

    try {
      await allocateLicense({ businessId, payload }).unwrap();
      navigate(`/businesses/${businessId}/licenses`, {
        state: { licenseUpdated: true },
      });
    } catch (err) {
      setSubmitError(err?.data?.detail || "Failed to update license.");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit License"
        subtitle={`Update license configuration for ${business?.businessName || "this business"}.`}
        actions={
          <Link to={`/businesses/${businessId}/licenses`}>
            <Button size="sm" variant="secondary">
              Back to Overview
            </Button>
          </Link>
        }
      />

      <Card>
        {isLicenseLoading && (
          <p className="text-sm text-slate-500">Loading current license...</p>
        )}
        {isLicenseError && (
          <Alert tone="error">
            {licenseError?.data?.detail || "Failed to load current license."}
          </Alert>
        )}

        {!isLicenseLoading && (
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Plan Code"
                name="planCode"
                value={form.planCode}
                onChange={handleChange}
                error={errors.planCode}
                placeholder="PRO"
                required
              />
              <Input
                label="Start Date"
                name="startsOn"
                type="date"
                value={form.startsOn}
                onChange={handleChange}
                error={errors.startsOn}
                required
              />
              <Input
                label="End Date"
                name="endsOn"
                type="date"
                value={form.endsOn}
                onChange={handleChange}
                error={errors.endsOn}
                required
              />
              <Input
                label="Grace Days"
                name="graceDays"
                type="number"
                min="0"
                value={form.graceDays}
                onChange={handleChange}
                error={errors.graceDays}
              />
              <Input
                label="Max Stores"
                name="maxStores"
                type="number"
                min="0"
                value={form.maxStores}
                onChange={handleChange}
                error={errors.maxStores}
                required
              />
              <Input
                label="Max Tills Per Store"
                name="maxTillsPerStore"
                type="number"
                min="0"
                value={form.maxTillsPerStore}
                onChange={handleChange}
                error={errors.maxTillsPerStore}
                required
              />
              <Input
                label="Max Kiosks Per Store"
                name="maxKiosksPerStore"
                type="number"
                min="0"
                value={form.maxKiosksPerStore}
                onChange={handleChange}
                error={errors.maxKiosksPerStore}
                required
              />
            </div>

            {submitError && <Alert tone="error">{submitError}</Alert>}

            <div className="flex gap-3">
              <Button type="submit" disabled={allocateState.isLoading}>
                {allocateState.isLoading ? "Saving..." : "Save License"}
              </Button>
              <Link to={`/businesses/${businessId}/licenses`}>
                <Button type="button" variant="secondary">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default EditLicense;
